// Module Import
//---------------------------------
import React, { useCallback, useEffect, useState } from 'react'
import PropTypes from 'prop-types'
import styled from 'styled-components'
import useDebounce from '../../../hooks/useDebounce'
import { brandOrange, brandSoftGrey, brandSoftOrange } from '../../../assets/consts/color'
import { secondaryFont } from '../../../assets/consts/fonts'

// Component Import
//---------------------------------

// Styled Component Declarations
//---------------------------------
const FieldWrapper = styled.div`
  display: flex;
`

const ActionButton = styled.button`
  color: ${brandOrange};
  background: none;
  outline: none;
  border: none;

  width: 32px;

  cursor: pointer;
  transition: all 0.2s ease-in-out;

  display: grid;
  place-content: center;

  position: relative;

  span {
    position: absolute;
    top: 48%;
    left: 50%;
    transform: translate(-50%, -50%);

    font-family: ${secondaryFont}, serif;
    font-size: 24px;
    font-weight: 600;
    height: 24px;
    line-height: 24px;
  }

  &:hover {
    background-color: ${brandSoftOrange};
  }
`

const InputField = styled.input`
  display: flex;
  padding: 16px 0px;
  background-color: transparent;
  align-self: stretch;
  //border: 2px solid #FB7347;
  border: none;
  border-bottom: 2px solid ${brandOrange};
  transition: all 0.25s;
  color: ${brandSoftGrey};

  outline: none;

  text-align: center;

  font-family: ${secondaryFont};
  font-size: 20px;
  font-style: normal;
  font-weight: 300;
  line-height: 100%; /* 20px */
  width: 64px;

  /* Chrome, Safari, Edge, Opera */

  &::-webkit-outer-spin-button,
  &::-webkit-inner-spin-button {
    -webkit-appearance: none;
    margin: 0;
  }

  /* Firefox */
  -moz-appearance: textfield;
`

const MAX_VALUE = 100
const MIN_VALUE = 1

// Component Export
//---------------------------------
export default function QuantityField({ value, onChange, max, ...prop }) {
  const [inputValue, setInputValue] = useState(value)

  useEffect(() => {
    setInputValue(value)
  }, [value])

  const handleChangeDebounced = useDebounce((_value) => {
    let newValue = Number(_value)

    if (newValue > (max ?? MAX_VALUE)) {
      newValue = max ?? MAX_VALUE
    }

    if (newValue < MIN_VALUE) {
      newValue = 1
    }

    onChange?.(newValue)

    if (value === newValue) {
      setInputValue(value)
    }
  }, 1000)

  const handleChange = useCallback(
    (event) => {
      const { value } = event.target

      if (/^\d*$/.test(value) && value.length <= 4) {
        setInputValue(value)

        handleChangeDebounced(value)
      }
    },
    [handleChangeDebounced]
  )

  const onIncrease = useCallback(() => {
    if (value + 1 > (max ?? MAX_VALUE)) {
      return
    }

    onChange?.(value + 1)
  }, [onChange, value, max])

  const onDecrease = useCallback(() => {
    if (value - 1 < MIN_VALUE) {
      return
    }

    onChange?.(value - 1)
  }, [onChange, value])

  return (
    <FieldWrapper>
      <ActionButton onClick={onDecrease}>
        <span>-</span>
      </ActionButton>
      <InputField
        type="number"
        value={inputValue}
        onChange={handleChange}
        min={MIN_VALUE}
        max={max ?? MAX_VALUE}
        {...prop}
      />
      <ActionButton onClick={onIncrease}>
        <span>+</span>
      </ActionButton>
    </FieldWrapper>
  )
}

QuantityField.propTypes = {
  value: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  max: PropTypes.number,
  onChange: PropTypes.func,
}
